import {
  Zoning,
  ZoningType,
  zoning_indice_sources,
  zoning_sources_agreement,
  ZoningReference,
  ZoningMetricSources,
} from '@popety-io/types'
import { ZoningLandData } from '../../hooks/useZoningMetrics'
import { getMetricUnit, convertValueForDisplay, formatMinMaxRange } from '../../utils/zoningHelpers'
import { getZonePlanTypeTitle, getZoneNameForHeader } from '../../utils/zoningDisplayHelpers'

/**
 * Mapping of metric keys to their corresponding land data fields
 */
const LAND_DATA_MAPPING: Record<string, 'max_height' | 'max_floor_nb' | 'unit_nb'> = {
  building_total_height: 'max_height',
  floor_nb: 'max_floor_nb',
  unit_nb: 'unit_nb',
}

/**
 * Gets the current value from land data for specific metrics
 * Maps metric keys to their corresponding land data fields
 */
export const getCurrentValueFromLandData = (
  metricKey: string,
  landData?: ZoningLandData
): number | undefined => {
  if (!landData) return undefined

  const landDataField = LAND_DATA_MAPPING[metricKey]
  return landDataField ? landData[landDataField] : undefined
}

/**
 * Metric data structure from getPopulatedMetrics
 */
export interface MetricData {
  key: string
  min?: number
  max?: number
  minSource?: string
  maxSource?: string
  minReference?: ZoningReference
  maxReference?: ZoningReference
}

/**
 * Props for MetricSourceModal component
 */
export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  metricLabel: string
  isRange: boolean
  // Range display props
  minValue?: number
  maxValue?: number
  minSource?: zoning_indice_sources
  maxSource?: zoning_indice_sources
  minReference?: ZoningReference
  maxReference?: ZoningReference
  minAdditionalInfo?: string
  maxAdditionalInfo?: string
  // Single value display props
  value?: number | string
  source?: zoning_indice_sources
  reference?: ZoningReference
  additionalInfo?: string
  unit?: string
  geoPortalLink?: string
  centerChProjection?: string
  geoCenter?: { lat: number; lon: number } | [number, number] | string
  geoPolygon?: { type: string; coordinates: number[][][] | number[][][][] }
  landGeoPolygon?: { type: string; coordinates: number[][][] | number[][][][] }
  regionName?: string
  // Zone context for reporting
  landId?: string
  zoneId?: string
  zoneName?: string
  planName?: string
  zoneType?: ZoningType
  municipalityName?: string
  // v2.0 multi-source data
  defaultSource?: zoning_indice_sources
  sourcesAgreement?: zoning_sources_agreement
  sources?: ZoningMetricSources
  // v2.0 per-bound data (for range metrics)
  minSources?: ZoningMetricSources
  maxSources?: ZoningMetricSources
  minDefaultSource?: zoning_indice_sources
  maxDefaultSource?: zoning_indice_sources
  minSourcesAgreement?: zoning_sources_agreement
  maxSourcesAgreement?: zoning_sources_agreement
}

/**
 * Gets the v2.0 multi-source field prefix for a metric key.
 * v2.0 ES data uses `{metric}_max_default_source`, `{metric}_max_sources`, etc.
 * The metric key from getPopulatedMetrics is the base (e.g., 'ius').
 * We try `{key}_max` first (most common), then `{key}_min`, then `{key}` (for _value-only metrics).
 */
const getV2FieldPrefix = (zone: Zoning, metricKey: string): string | undefined => {
  const z = zone as any
  const candidates = [`${metricKey}_max`, `${metricKey}_min`, metricKey]
  for (const prefix of candidates) {
    if (z[`${prefix}_default_source`] !== undefined) return prefix
  }
  return undefined
}

/**
 * Resolves a geoportal URL template by replacing {x} and {y} placeholders
 * with EPSG:2056 coordinates from the land's center_ch_projection.
 */
const resolveGeoportalUrl = (
  urlTemplate?: string,
  centerChProjection?: string
): string | undefined => {
  if (!urlTemplate) return undefined
  if (!urlTemplate.includes('{x}') && !urlTemplate.includes('{y}')) return urlTemplate
  if (!centerChProjection) return undefined

  const parts = centerChProjection.split(',')
  if (parts.length !== 2) return undefined

  const [x, y] = parts.map((p) => p.trim())
  return urlTemplate.replace(/\{x\}/g, x).replace(/\{y\}/g, y)
}

/**
 * Builds the props object for MetricSourceModal based on the selected metric
 * Handles both range display (min + max) and single value display (max only or min only)
 */
export const getModalProps = (
  selectedMetric: MetricData,
  zone: Zoning,
  metricLabel: string,
  isOpen: boolean,
  onClose: () => void,
  landData?: ZoningLandData,
  zoningType?: ZoningType,
  landId?: string
): ModalProps => {
  const hasMinMax = selectedMetric.min !== undefined && selectedMetric.max !== undefined
  const hasMaxOnly = selectedMetric.max !== undefined && selectedMetric.min === undefined
  const hasMinOnly = selectedMetric.min !== undefined && selectedMetric.max === undefined

  // Extract v2.0 multi-source fields from zone
  const v2Prefix = getV2FieldPrefix(zone, selectedMetric.key)
  const zoneData = zone as any
  const v2DefaultSource = v2Prefix
    ? (zoneData[`${v2Prefix}_default_source`] as zoning_indice_sources | undefined)
    : undefined
  const v2Agreement = v2Prefix
    ? (zoneData[`${v2Prefix}_sources_agreement`] as zoning_sources_agreement | undefined)
    : undefined
  const v2Sources = v2Prefix
    ? (zoneData[`${v2Prefix}_sources`] as ZoningMetricSources | undefined)
    : undefined

  return {
    isOpen,
    onClose,
    metricLabel,
    isRange: hasMinMax,
    // Range display props (convert percentage values for display)
    minValue: hasMinMax
      ? convertValueForDisplay(selectedMetric.min, selectedMetric.key)
      : undefined,
    maxValue: hasMinMax
      ? convertValueForDisplay(selectedMetric.max, selectedMetric.key)
      : undefined,
    minSource: hasMinMax ? (selectedMetric.minSource as zoning_indice_sources) : undefined,
    maxSource: hasMinMax ? (selectedMetric.maxSource as zoning_indice_sources) : undefined,
    minReference: hasMinMax ? selectedMetric.minReference : undefined,
    maxReference: hasMinMax ? selectedMetric.maxReference : undefined,
    minAdditionalInfo: hasMinMax
      ? (zone[`${selectedMetric.key}_min_additional_info` as keyof Zoning] as string | undefined)
      : undefined,
    maxAdditionalInfo: hasMinMax
      ? (zone[`${selectedMetric.key}_max_additional_info` as keyof Zoning] as string | undefined)
      : undefined,
    // Single value display props (convert percentage values for display)
    value: hasMinMax
      ? formatMinMaxRange(
          convertValueForDisplay(selectedMetric.min, selectedMetric.key),
          convertValueForDisplay(selectedMetric.max, selectedMetric.key)
        )
      : hasMaxOnly
        ? convertValueForDisplay(selectedMetric.max, selectedMetric.key)
        : hasMinOnly
          ? convertValueForDisplay(selectedMetric.min, selectedMetric.key)
          : v2DefaultSource
            ? 'N/A'
            : undefined,
    source: hasMaxOnly
      ? (selectedMetric.maxSource as zoning_indice_sources)
      : hasMinOnly
        ? (selectedMetric.minSource as zoning_indice_sources)
        : undefined,
    reference: hasMaxOnly
      ? selectedMetric.maxReference
      : hasMinOnly
        ? selectedMetric.minReference
        : undefined,
    additionalInfo: hasMaxOnly
      ? (zone[`${selectedMetric.key}_max_additional_info` as keyof Zoning] as string | undefined)
      : hasMinOnly
        ? (zone[`${selectedMetric.key}_min_additional_info` as keyof Zoning] as string | undefined)
        : undefined,
    unit: getMetricUnit(selectedMetric.key),
    geoPortalLink:
      resolveGeoportalUrl((zone as any).geoportal_url, landData?.centerChProjection) ||
      landData?.geoPortalLink,
    centerChProjection: landData?.centerChProjection,
    geoCenter: landData?.geoCenter,
    geoPolygon: (zone as any).geo_polygon as
      { type: string; coordinates: number[][][] | number[][][][] } | undefined,
    landGeoPolygon: landData?.landGeoPolygon,
    regionName: zone.region_name,
    // Zone context for reporting
    landId,
    zoneId: zone._id,
    zoneName: zoningType ? getZonePlanTypeTitle(zone, zoningType) : zone.region_name,
    planName: zoningType ? getZoneNameForHeader(zone, zoningType) : undefined,
    zoneType: zoningType,
    municipalityName: zone.municipality_name,
    // v2.0 multi-source data
    defaultSource: v2DefaultSource,
    sourcesAgreement: v2Agreement,
    sources: v2Sources,
    // v2.0 per-bound data (populated for range metrics)
    minSources: hasMinMax
      ? ((zone as any)[`${selectedMetric.key}_min_sources`] as ZoningMetricSources | undefined)
      : undefined,
    maxSources: hasMinMax
      ? ((zone as any)[`${selectedMetric.key}_max_sources`] as ZoningMetricSources | undefined)
      : undefined,
    minDefaultSource: hasMinMax
      ? ((zone as any)[`${selectedMetric.key}_min_default_source`] as
          zoning_indice_sources | undefined)
      : undefined,
    maxDefaultSource: hasMinMax
      ? ((zone as any)[`${selectedMetric.key}_max_default_source`] as
          zoning_indice_sources | undefined)
      : undefined,
    minSourcesAgreement: hasMinMax
      ? ((zone as any)[`${selectedMetric.key}_min_sources_agreement`] as
          zoning_sources_agreement | undefined)
      : undefined,
    maxSourcesAgreement: hasMinMax
      ? ((zone as any)[`${selectedMetric.key}_max_sources_agreement`] as
          zoning_sources_agreement | undefined)
      : undefined,
  }
}

/**
 * Gets the v2.0 default source for a metric's MIN value from zone data.
 * Tries {metric}_min_default_source first, then falls back to v1.0 {metric}_min_source.
 */
export const getMetricMinDefaultSource = (
  zone: Zoning,
  metricKey: string
): zoning_indice_sources | undefined => {
  const z = zone as any
  const minSource = z[`${metricKey}_min_default_source`]
  if (minSource) return minSource as zoning_indice_sources
  return z[`${metricKey}_min_source`] as zoning_indice_sources | undefined
}

/**
 * Gets the v2.0 default source for a metric's MAX value from zone data.
 * Tries {metric}_max_default_source first, then falls back to v1.0 {metric}_max_source.
 */
export const getMetricMaxDefaultSource = (
  zone: Zoning,
  metricKey: string
): zoning_indice_sources | undefined => {
  const z = zone as any
  const maxSource = z[`${metricKey}_max_default_source`]
  if (maxSource) return maxSource as zoning_indice_sources
  return z[`${metricKey}_max_source`] as zoning_indice_sources | undefined
}

/**
 * Gets the v2.0 default source for a metric from zone data
 */
export const getMetricDefaultSource = (
  zone: Zoning,
  metricKey: string
): zoning_indice_sources | undefined => {
  const prefix = getV2FieldPrefix(zone, metricKey)
  if (!prefix) return undefined
  return (zone as any)[`${prefix}_default_source`] as zoning_indice_sources | undefined
}

/**
 * Formats a numeric value for display in the badge line (conflict strikethrough).
 * Uses the same formatting logic as the main value display.
 */
const formatMetricValueForBadgeLine = (value: number, metricKey: string): string => {
  const unit = getMetricUnit(metricKey)
  const displayValue = convertValueForDisplay(value, metricKey)
  if (displayValue === undefined) return String(value)
  return unit ? `${displayValue} ${unit}` : String(displayValue)
}

/**
 * Gets the v2.0 sources agreement type for a metric from zone data
 */
export const getMetricAgreement = (
  zone: Zoning,
  metricKey: string
): zoning_sources_agreement | undefined => {
  const prefix = getV2FieldPrefix(zone, metricKey)
  if (!prefix) return undefined
  return (zone as any)[`${prefix}_sources_agreement`] as zoning_sources_agreement | undefined
}

/**
 * Gets the v2.0 sources agreement for the MIN bound.
 */
export const getMetricMinAgreement = (
  zone: Zoning,
  metricKey: string
): zoning_sources_agreement | undefined => {
  return (zone as any)[`${metricKey}_min_sources_agreement`] as zoning_sources_agreement | undefined
}

/**
 * Gets the v2.0 sources agreement for the MAX bound.
 */
export const getMetricMaxAgreement = (
  zone: Zoning,
  metricKey: string
): zoning_sources_agreement | undefined => {
  return (zone as any)[`${metricKey}_max_sources_agreement`] as zoning_sources_agreement | undefined
}

/**
 * Gets the v2.0 sources object for a metric from zone data
 */
export const getMetricSources = (
  zone: Zoning,
  metricKey: string
): ZoningMetricSources | undefined => {
  const prefix = getV2FieldPrefix(zone, metricKey)
  if (!prefix) return undefined
  return (zone as any)[`${prefix}_sources`] as ZoningMetricSources | undefined
}

/**
 * Gets the v2.0 sources object for the MIN bound of a metric.
 * Returns undefined if `{metric}_min_sources` is not present.
 */
export const getMetricMinSources = (
  zone: Zoning,
  metricKey: string
): ZoningMetricSources | undefined => {
  return (zone as any)[`${metricKey}_min_sources`] as ZoningMetricSources | undefined
}

/**
 * Gets the v2.0 sources object for the MAX bound of a metric.
 * Returns undefined if `{metric}_max_sources` is not present.
 */
export const getMetricMaxSources = (
  zone: Zoning,
  metricKey: string
): ZoningMetricSources | undefined => {
  return (zone as any)[`${metricKey}_max_sources`] as ZoningMetricSources | undefined
}

/**
 * Returns which badges to show for a metric, applying ALG suppression.
 * ALG is hidden when REG (llm) or GEO (geoportal) exists.
 */
export const getVisibleSources = (sources?: ZoningMetricSources): zoning_indice_sources[] => {
  if (!sources) return []
  const available: zoning_indice_sources[] = []
  const hasLlm = sources.llm?.value !== undefined
  const hasGeoportal = sources.geoportal?.value !== undefined
  const hasEstimate = sources.estimate?.value !== undefined
  const hasManual = sources.manual?.value !== undefined

  if (hasGeoportal) available.push('geoportal')
  if (hasLlm) available.push('llm')
  // ALG suppression: only show estimate if no llm or geoportal
  if (hasEstimate && !hasLlm && !hasGeoportal) available.push('estimate')
  // manual included for completeness; SourceBadgeLine handles it via early-return
  if (hasManual) available.push('manual')

  return available
}

/**
 * Gets the conflicting source value (the non-default source's value)
 * for display as strikethrough in the table badge line.
 */
export const getConflictValue = (
  sources: ZoningMetricSources | undefined,
  defaultSource: zoning_indice_sources | undefined,
  metricKey: string
): { source: zoning_indice_sources; value: string } | undefined => {
  if (!sources || !defaultSource) return undefined
  const visible = getVisibleSources(sources)
  // ALG suppression guarantees at most 2 non-manual sources
  const conflicting = visible.find((s) => s !== defaultSource && s !== 'manual')
  if (!conflicting) return undefined
  const srcData =
    conflicting === 'llm'
      ? sources.llm
      : conflicting === 'geoportal'
        ? sources.geoportal
        : sources.estimate
  if (!srcData?.value) return undefined
  return {
    source: conflicting,
    value: formatMetricValueForBadgeLine(srcData.value, metricKey),
  }
}
